import InboxOutlined from '@ant-design/icons/InboxOutlined'
import type { UploadFile } from 'antd'
import { Alert, Button, message, Progress, Upload } from 'antd'
import { useGeckoAuth } from 'features/auth'
import { useGetBatchRolodexUploadUrls, useRolodexEdit } from 'features/fde'
import {
  isWFile,
  validateFiles,
  ValidationResult,
} from 'features/flightDataUpload/utils/fileValidation'
import {
  AsyncLock,
  formatBytes,
  formatTime,
  retryWithBackoff,
  runWithConcurrencyLimit,
} from 'features/flightDataUpload/utils/uploadHelpers'
import { useRolodexEntries } from 'features/rolodex'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FixedSizeList as List } from 'react-window'
import styled from 'styled-components'
import {
  FlightSetType,
  RolodexEntryPutJson,
  RolodexEntryType,
  RooftopFlightDataData,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

const { Dragger } = Upload

const UploaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
`

const StyledDragger = styled(Dragger)`
  .ant-upload-drag {
    background-color: ${({ theme }) => theme.forecast.background2};
    border: 2px dashed ${({ theme }) => theme.forecast.background4};

    &:hover {
      border-color: ${({ theme }) => theme.forecast.text2};
    }
  }

  .ant-upload-drag-icon {
    color: ${({ theme }) => theme.forecast.text2};
  }

  .ant-upload-text {
    color: ${({ theme }) => theme.forecast.text1};
  }

  .ant-upload-hint {
    color: ${({ theme }) => theme.forecast.text3};
  }
`

const ValidationSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`

const FileListContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.background4};
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.forecast.background2};
  max-height: 400px;
  overflow: hidden;
`

const FileListHeader = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.forecast.background4};
  background-color: ${({ theme }) => theme.forecast.background3};
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.text1};
  font-size: 0.875rem;
`

const FileItem = styled.div<{ $isAlreadyUploaded?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  background-color: ${({ $isAlreadyUploaded }) =>
    $isAlreadyUploaded ? 'rgba(82, 196, 26, 0.1)' : 'transparent'};
  border: ${({ $isAlreadyUploaded }) =>
    $isAlreadyUploaded ? '1px solid rgba(82, 196, 26, 0.3)' : 'none'};
  border-radius: 4px;
  margin: 4px 8px;
`

const FileItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const FileName = styled.span`
  color: ${({ theme }) => theme.forecast.text1};
  font-size: 0.875rem;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StatusBadge = styled.span<{ $type: 'uploaded' | 'already' }>`
  color: #52c41a;
  font-weight: bold;
  font-size: 0.875rem;
  white-space: nowrap;
`

const VALIDATION_DEBOUNCE_MS = 250
const VALIDATION_CHUNK_SIZE = 200
const SIGNED_URL_BATCH_SIZE = 100

type WakeLockSentinelType = Awaited<
  ReturnType<NonNullable<Navigator['wakeLock']>['request']>
>

interface SelectedFile {
  uid: string
  file: File
}

interface PhotoUploaderProps {
  componentId: string
  assetId: string
  siteId: string
  organizationId: string
  flightSetType: FlightSetType
  weatherType: string
  notes: string
  onUploadComplete: (entryId: string) => void
}

export const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  componentId,
  assetId,
  siteId,
  organizationId,
  flightSetType,
  weatherType,
  notes,
  onUploadComplete,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null)
  const [isValidating, setIsValidating] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadedCount, setUploadedCount] = useState(0)
  const [overrideValidation, setOverrideValidation] = useState(false)
  const [fileUploadStatus, setFileUploadStatus] = useState<
    Record<string, 'pending' | 'uploading' | 'success' | 'error'>
  >({})
  const [fileUploadProgress, setFileUploadProgress] = useState<
    Record<string, number>
  >({})
  const [uploadSpeed, setUploadSpeed] = useState<number>(0)
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<
    number | null
  >(null)

  const hasShownWFileWarningRef = useRef(false)
  const uploadStartTimeRef = useRef<number>(0)
  const totalBytesUploadedRef = useRef<number>(0)
  const validationRequestIdRef = useRef(0)
  const validationTimeoutIdRef = useRef<number>(undefined)
  const lastValidatedChunkRef = useRef<number>(0)
  const wakeLockRef = useRef<WakeLockSentinelType | null>(null)
  const isWakeLockSupported = useMemo(
    () => typeof navigator !== 'undefined' && 'wakeLock' in navigator,
    []
  )

  const { rolodexPut, rolodexPatch } = useRolodexEdit()
  const getBatchUploadUrls = useGetBatchRolodexUploadUrls()
  const { authorizedAxios: axios } = useGeckoAuth()

  const { entries: existingEntries, forceRefresh } = useRolodexEntries({
    type: [RolodexEntryType.ROOFTOP_FLIGHT_DATA],
    tags: {
      [RolodexEntryType.COMPONENT]: componentId,
    },
  })

  const existingEntry = useMemo(() => {
    const entry = existingEntries.find(
      (e) => (e.data as RooftopFlightDataData).flight_set_type === flightSetType
    )
    if (!entry) return undefined
    return {
      ...entry,
      data: entry.data as RooftopFlightDataData,
    }
  }, [existingEntries, flightSetType])

  const entryId = useMemo(() => {
    if (existingEntry) {
      return existingEntry.id
    }
    const componentHash = componentId.slice(-8)
    const uniqueHash = uuidv4().slice(0, 6)
    return `rfd-${componentHash}-${flightSetType}-${uniqueHash}`
  }, [existingEntry, componentId, flightSetType])

  const alreadyUploadedPhotos = useMemo(
    () => new Set(existingEntry?.data.uploaded_photos || []),
    [existingEntry]
  )

  useEffect(() => {
    if (validationTimeoutIdRef.current !== undefined) {
      clearTimeout(validationTimeoutIdRef.current)
    }

    if (fileList.length === 0) {
      validationRequestIdRef.current += 1
      setIsValidating(false)
      setValidationResult(null)
      lastValidatedChunkRef.current = 0
      return
    }

    const requestId = validationRequestIdRef.current + 1
    validationRequestIdRef.current = requestId

    validationTimeoutIdRef.current = Number(
      setTimeout(async () => {
        setIsValidating(true)
        try {
          const preparedFiles: SelectedFile[] = fileList
            .map((uploadFile) => ({
              file: uploadFile.originFileObj,
              uid: uploadFile.uid,
            }))
            .filter((item) => item.file !== undefined) as SelectedFile[]

          if (preparedFiles.length === 0) {
            if (validationRequestIdRef.current === requestId) {
              setValidationResult(null)
            }
            return
          }

          const nextChunkUpperBound = Math.min(
            preparedFiles.length,
            lastValidatedChunkRef.current + VALIDATION_CHUNK_SIZE
          )
          const result = await validateFiles(
            preparedFiles
              .slice(lastValidatedChunkRef.current, nextChunkUpperBound)
              .map((item) => item.file),
            flightSetType
          )

          lastValidatedChunkRef.current = nextChunkUpperBound
          if (validationRequestIdRef.current === requestId) {
            setValidationResult(result)
          }
        } finally {
          if (validationRequestIdRef.current === requestId) {
            setIsValidating(false)
          }
        }
      }, VALIDATION_DEBOUNCE_MS)
    )

    return () => {
      if (validationTimeoutIdRef.current !== undefined) {
        clearTimeout(validationTimeoutIdRef.current)
      }
    }
  }, [fileList, flightSetType])

  useEffect(() => {
    hasShownWFileWarningRef.current = false
    setOverrideValidation(false)
  }, [flightSetType])

  const handleFilesChange = (info: { fileList: UploadFile[] }) => {
    if (flightSetType === 'day-ir' || flightSetType === 'night-ir') {
      const originalCount = info.fileList.length
      const filteredFileList = info.fileList.filter(
        (file) => !isWFile(file.name)
      )
      const removedCount = originalCount - filteredFileList.length

      if (removedCount > 0 && !hasShownWFileWarningRef.current) {
        message.warning(
          `${removedCount} RGB (_W) file(s) were automatically filtered out for IR flight set`
        )
        hasShownWFileWarningRef.current = true
      }

      setFileList(filteredFileList)
    } else {
      setFileList(info.fileList)
    }
    setValidationResult(null)
    setOverrideValidation(false)
  }

  const handleUpload = useCallback(async () => {
    if (!validationResult?.isValid && !overrideValidation) {
      message.error(
        'Please fix validation errors or click "Upload Anyway" to override'
      )
      return
    }

    setIsUploading(true)
    setUploadedCount(0)
    setUploadSpeed(0)
    setEstimatedTimeRemaining(null)
    uploadStartTimeRef.current = Date.now()
    totalBytesUploadedRef.current = 0

    try {
      if (isWakeLockSupported) {
        try {
          wakeLockRef.current = await navigator.wakeLock?.request('screen')
        } catch (error) {
          console.warn('Unable to acquire wake lock', error)
        }
      }

      const files = fileList
        .map((f) => f.originFileObj)
        .filter((f) => f !== undefined) as File[]

      const filesToUpload = files.filter(
        (file) => !alreadyUploadedPhotos.has(file.name)
      )

      if (filesToUpload.length === 0) {
        message.info('All photos have already been uploaded')
        setIsUploading(false)
        return
      }

      const totalBytes = filesToUpload.reduce((sum, file) => sum + file.size, 0)

      const initialStatus: Record<
        string,
        'pending' | 'uploading' | 'success' | 'error'
      > = {}
      const initialProgress: Record<string, number> = {}

      fileList.forEach((file) => {
        if (!alreadyUploadedPhotos.has(file.name)) {
          initialStatus[file.uid] = 'pending'
          initialProgress[file.uid] = 0
        }
      })

      setFileUploadStatus(initialStatus)
      setFileUploadProgress(initialProgress)

      if (!existingEntry) {
        const newEntry: RolodexEntryPutJson = {
          id: entryId,
          type: RolodexEntryType.ROOFTOP_FLIGHT_DATA,
          data: {
            component_id: componentId,
            flight_set_type: flightSetType,
            weather_type: weatherType,
            upload_date: new Date().toISOString(),
            total_photos: files.length,
            uploaded_photos: [],
            metadata: { notes },
          },
          tags: {
            [RolodexEntryType.COMPONENT]: componentId,
            [RolodexEntryType.UNIT]: assetId,
            [RolodexEntryType.SITE]: siteId,
            [RolodexEntryType.ORGANIZATION]: organizationId,
          },
        }
        await rolodexPut([newEntry])
      }

      const uploadedPhotos: string[] = [...alreadyUploadedPhotos]
      let completedCount = 0
      const lock = new AsyncLock()

      const CONCURRENT_UPLOADS = 3

      const uploadResults: Array<{ success: boolean; filename: string }> = []

      for (
        let startIndex = 0;
        startIndex < filesToUpload.length;
        startIndex += SIGNED_URL_BATCH_SIZE
      ) {
        const chunk = filesToUpload.slice(
          startIndex,
          startIndex + SIGNED_URL_BATCH_SIZE
        )

        if (chunk.length === 0) {
          continue
        }

        const chunkSignedUrls = await getBatchUploadUrls(
          entryId,
          chunk.map((file) => `photos/${file.name}`)
        )

        const chunkResults = await runWithConcurrencyLimit(
          chunk,
          async (file) => {
            const fileListItem = fileList.find((f) => f.name === file.name)

            if (fileListItem) {
              setFileUploadStatus((prev) => ({
                ...prev,
                [fileListItem.uid]: 'uploading',
              }))
            }

            try {
              const fileStartSize = totalBytesUploadedRef.current
              const signedUrl = chunkSignedUrls[`photos/${file.name}`]

              if (!signedUrl) {
                throw new Error(`Missing signed URL for ${file.name}`)
              }

              await retryWithBackoff(async () => {
                await axios.put(signedUrl, file, {
                  headers: {
                    'Content-Type': 'application/octet-stream',
                  },
                  onUploadProgress: (progressEvent) => {
                    if (progressEvent.total && fileListItem) {
                      const progress = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                      )
                      setFileUploadProgress((prev) => ({
                        ...prev,
                        [fileListItem.uid]: progress,
                      }))

                      const elapsedSeconds =
                        (Date.now() - uploadStartTimeRef.current) / 1000
                      const bytesForThisFile = (file.size * progress) / 100
                      const currentTotalBytes = fileStartSize + bytesForThisFile
                      const speed = currentTotalBytes / elapsedSeconds
                      const remainingBytes = totalBytes - currentTotalBytes

                      setUploadSpeed(speed)
                      setEstimatedTimeRemaining(
                        speed > 0 ? remainingBytes / speed : null
                      )
                    }
                  },
                })
              })

              await lock.runExclusive(async () => {
                totalBytesUploadedRef.current += file.size
                uploadedPhotos.push(file.name)
                completedCount++
                setUploadedCount(completedCount)

                if (fileListItem) {
                  setFileUploadStatus((prev) => ({
                    ...prev,
                    [fileListItem.uid]: 'success',
                  }))
                  setFileUploadProgress((prev) => ({
                    ...prev,
                    [fileListItem.uid]: 100,
                  }))
                }

                await rolodexPatch([
                  {
                    id: entryId,
                    data: {
                      uploaded_photos: [...uploadedPhotos],
                      total_photos: files.length,
                    },
                  },
                ])
              })

              return { success: true, filename: file.name }
            } catch (error) {
              if (fileListItem) {
                setFileUploadStatus((prev) => ({
                  ...prev,
                  [fileListItem.uid]: 'error',
                }))
              }
              message.error(`Failed to upload ${file.name}: ${String(error)}`)
              return { success: false, filename: file.name }
            }
          },
          CONCURRENT_UPLOADS
        )

        uploadResults.push(...chunkResults)
      }

      const failedUploads = uploadResults.filter((r) => !r.success)
      if (failedUploads.length > 0) {
        message.warning(
          `${failedUploads.length} file(s) failed to upload. You can retry by uploading the same files again.`
        )
      }

      await forceRefresh()
      message.success('Upload completed successfully!')
      onUploadComplete(entryId)
      setFileList([])
      setValidationResult(null)
      setFileUploadStatus({})
      setFileUploadProgress({})
      setUploadSpeed(0)
      setEstimatedTimeRemaining(null)
    } catch (error) {
      message.error(`Upload failed: ${String(error)}`)
    } finally {
      if (wakeLockRef.current) {
        try {
          await wakeLockRef.current.release()
        } catch (releaseError) {
          console.warn('Failed to release wake lock', releaseError)
        }
        wakeLockRef.current = null
      }

      setIsUploading(false)
      setUploadedCount(0)
    }
  }, [
    validationResult,
    overrideValidation,
    fileList,
    flightSetType,
    alreadyUploadedPhotos,
    existingEntry,
    entryId,
    componentId,
    weatherType,
    notes,
    assetId,
    siteId,
    organizationId,
    rolodexPut,
    rolodexPatch,
    getBatchUploadUrls,
    axios,
    forceRefresh,
    onUploadComplete,
    isWakeLockSupported,
  ])

  const handleClear = () => {
    setFileList([])
    setValidationResult(null)
    setOverrideValidation(false)
    setFileUploadStatus({})
    setFileUploadProgress({})
    setUploadSpeed(0)
    setEstimatedTimeRemaining(null)
    hasShownWFileWarningRef.current = false
    uploadStartTimeRef.current = 0
    totalBytesUploadedRef.current = 0
  }

  return (
    <UploaderContainer>
      {existingEntry && (
        <Alert
          title='Existing Flight Data Found'
          description={`This component already has ${existingEntry.data.uploaded_photos.length} photos uploaded for this flight set. New uploads will be added to the existing set.`}
          type='info'
          showIcon
        />
      )}

      <StyledDragger
        multiple
        fileList={[]}
        showUploadList={false}
        onChange={handleFilesChange}
        beforeUpload={() => false}
        disabled={isUploading}
        accept='image/jpeg,image/jpg'>
        <p className='ant-upload-drag-icon'>
          <InboxOutlined />
        </p>
        <p className='ant-upload-text'>
          Click or drag photos to this area to upload
        </p>
        <p className='ant-upload-hint'>
          Support for single or bulk upload. Drag folders directly for large
          sets.
        </p>
      </StyledDragger>

      {fileList.length > 0 && (
        <FileListContainer>
          <FileListHeader>
            {fileList.length} file{fileList.length !== 1 ? 's' : ''} selected
          </FileListHeader>
          <List
            height={Math.min(400, fileList.length * 60)}
            itemCount={fileList.length}
            itemSize={60}
            width='100%'>
            {({ index, style }) => {
              const file = fileList[index]
              const isAlreadyUploaded = alreadyUploadedPhotos.has(file.name)
              const status = fileUploadStatus[file.uid]
              const progress = fileUploadProgress[file.uid] || 0

              return (
                <div style={style}>
                  <FileItem $isAlreadyUploaded={isAlreadyUploaded}>
                    <FileItemContent>
                      {isAlreadyUploaded && (
                        <StatusBadge $type='already'>
                          ✓ Already Uploaded
                        </StatusBadge>
                      )}
                      {status === 'success' && !isAlreadyUploaded && (
                        <StatusBadge $type='uploaded'>✓ Uploaded</StatusBadge>
                      )}
                      <FileName>{file.name}</FileName>
                    </FileItemContent>
                    {status === 'uploading' && (
                      <Progress
                        percent={progress}
                        status='active'
                        showInfo={true}
                        style={{ marginTop: '4px' }}
                        strokeColor='#1890ff'
                        size='small'
                      />
                    )}
                    {status === 'error' && (
                      <span
                        style={{
                          color: '#ff4d4f',
                          fontSize: '0.75rem',
                          marginTop: '4px',
                        }}>
                        Upload failed
                      </span>
                    )}
                  </FileItem>
                </div>
              )
            }}
          </List>
        </FileListContainer>
      )}

      {isValidating && <Alert title='Validating photos...' type='info' />}

      {validationResult && (
        <ValidationSummary>
          {validationResult.warnings.length > 0 && (
            <Alert
              title='Warnings'
              description={
                <ul>
                  {validationResult.warnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              }
              type='warning'
              showIcon
            />
          )}

          {validationResult.errors.length > 0 && (
            <Alert
              title='Validation Errors'
              description={
                <ul>
                  {validationResult.errors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              }
              type='error'
              showIcon
            />
          )}

          {validationResult.isValid && (
            <Alert
              title='Validation Successful'
              description={
                <>
                  {(() => {
                    const alreadyUploadedCount = fileList.filter((f) =>
                      alreadyUploadedPhotos.has(f.name)
                    ).length
                    const toUploadCount = fileList.length - alreadyUploadedCount
                    const totalCount = fileList.length

                    if (alreadyUploadedCount > 0) {
                      return (
                        <>
                          <strong>
                            {toUploadCount} of {totalCount}
                          </strong>{' '}
                          photo(s) will be uploaded.{' '}
                          <strong>{alreadyUploadedCount}</strong> already
                          uploaded (will be skipped).
                        </>
                      )
                    }
                    return (
                      <>
                        All <strong>{toUploadCount}</strong> photo(s) will be
                        uploaded.
                      </>
                    )
                  })()}
                </>
              }
              type='success'
              showIcon
            />
          )}
        </ValidationSummary>
      )}

      {isUploading && (
        <Alert
          title='Uploading Photos'
          description={
            <div>
              <div>
                Uploading {uploadedCount} of{' '}
                {
                  fileList.filter((f) => !alreadyUploadedPhotos.has(f.name))
                    .length
                }{' '}
                photos
              </div>
              {uploadSpeed > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  <strong>Speed:</strong> {formatBytes(uploadSpeed)}/s
                  {estimatedTimeRemaining !== null &&
                    estimatedTimeRemaining > 0 && (
                      <>
                        {' '}
                        • <strong>Est. time:</strong>{' '}
                        {formatTime(estimatedTimeRemaining)}
                      </>
                    )}
                </div>
              )}
            </div>
          }
          type='info'
          showIcon
        />
      )}

      <ButtonGroup>
        {fileList.length > 0 && !isUploading && (
          <>
            <Button onClick={handleClear}>Clear</Button>
            {validationResult?.isValid ? (
              <Button type='primary' onClick={handleUpload}>
                Upload Photos
              </Button>
            ) : (
              <>
                <Button
                  type='primary'
                  onClick={handleUpload}
                  disabled={true}
                  title='Fix validation errors first'>
                  Upload Photos
                </Button>
                <Button
                  danger
                  onClick={() => {
                    setOverrideValidation(true)
                    setTimeout(() => handleUpload(), 100)
                  }}
                  title='Upload despite validation errors - use with caution'>
                  Upload Anyway (Override)
                </Button>
              </>
            )}
          </>
        )}
      </ButtonGroup>
    </UploaderContainer>
  )
}
